// =============================================================================
// Admin Analytics — GraphQL adapter (Activation only)
// =============================================================================
// The live implementation of `AdminAnalyticsAdapter`, passed into
// `AdminAnalyticsProvider` by `router.tsx`. Only the Activation tab still goes
// through the adapter — the Reports tab reads the `adminAnalytics` resource
// directly via the generated hooks (see `data/hooks.ts`).
//
// `providerActivation` is NOT implemented on the backend — there is no
// resolver for it — so `getInviteFunnel`/`getActivationProviders` will error
// if invoked; the Activation view has no real data source yet. Its query stays
// hand-written and untagged since there's no live schema to codegen against.
// =============================================================================
import { useMemo } from "react";

import { useCustomFetcher } from "@api/graphql/useCustomFetcher";
import {
  ActivationProviderRow,
  AdminAnalyticsAdapter,
  InviteFunnel,
  InviteStatus,
} from "@pages/AdminAnalytics/data/types";

type BeInviteStatus = "SENT" | "ACCEPTED" | "EXPIRED";
const INVITE_STATUS_MAP: Record<BeInviteStatus, InviteStatus> = {
  SENT: "sent",
  ACCEPTED: "accepted",
  EXPIRED: "expired",
};

interface ProviderActivationResponse {
  providerActivation: {
    funnel: { invited: number; expired: number; loggedIn: number; notYetLoggedIn: number } | null;
    providers: Array<{
      providerId: string;
      name: string;
      email: string;
      invitedAt: string;
      status: BeInviteStatus;
      lastLoginAt: string | null;
      hasLoggedIn: boolean;
    }>;
  } | null;
}

const PROVIDER_ACTIVATION_QUERY = `
  query providerActivation($fromDate: Date!, $toDate: Date!) {
    providerActivation(fromDate: $fromDate, toDate: $toDate) {
      funnel { invited expired loggedIn notYetLoggedIn }
      providers { providerId name email invitedAt status lastLoginAt hasLoggedIn }
    }
  }
`;

/**
 * Builds the GraphQL-backed adapter. Must be called from within React (it wires
 * `useCustomFetcher`, which needs the auth context). Pass the returned adapter
 * into `<AdminAnalyticsProvider adapter={...}>`.
 */
export const useGraphqlAdminAnalyticsAdapter = (): AdminAnalyticsAdapter => {
  const fetchActivation = useCustomFetcher<ProviderActivationResponse, { fromDate: string; toDate: string }>(
    PROVIDER_ACTIVATION_QUERY,
  );

  return useMemo<AdminAnalyticsAdapter>(
    () => ({
      async getInviteFunnel(filters) {
        const res = await fetchActivation({ fromDate: filters.range.start, toDate: filters.range.end });
        const f = res.providerActivation?.funnel;
        return {
          invited: f?.invited ?? 0,
          expired: f?.expired ?? 0,
          loggedIn: f?.loggedIn ?? 0,
          notYetLoggedIn: f?.notYetLoggedIn ?? 0,
        } satisfies InviteFunnel;
      },

      async getActivationProviders(filters) {
        const res = await fetchActivation({ fromDate: filters.range.start, toDate: filters.range.end });
        return (res.providerActivation?.providers ?? [])
          .map(
            (p): ActivationProviderRow => ({
              providerId: p.providerId,
              name: p.name,
              email: p.email,
              invitedAt: p.invitedAt,
              status: INVITE_STATUS_MAP[p.status],
              lastLoginAt: p.lastLoginAt ?? undefined,
              hasLoggedIn: p.hasLoggedIn,
            }),
          )
          .sort((a, b) => b.invitedAt.localeCompare(a.invitedAt));
      },
    }),
    [fetchActivation],
  );
};
